import { useRef, useEffect, useCallback, useMemo } from 'react';
import { ButtonGroup, Button, Menu, MenuContent, MenuTrigger, MenuItem } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useWatch } from 'react-hook-form';
import { v4 } from 'uuid';
import {
    ConsolidatedLocationView,
    EventPanelFormType,
    ProQaSettingForAgencyType,
} from '@mark43/cad-api';
import { useApiClient } from '@m43/core';

import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { globalAttributes } from '../../common/global-attributes';
import { useIntegratedActionBroadcaster } from '../../action-broadcaster/use-integrated-action-broadcaster';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { usePhoneCallsStore } from '../../phone-calls/use-phone-calls';
import type { ProQaMessageTypeLegacyElectron } from '../../common/types';
import type { EventInfo } from '../../common/broadcast-channel/types';
import { useEventMetadataStore } from './event-metadata';
import { storageHelpers } from '../../common/helpers/browser-storage';
import {
    DISCIPLINE_LABEL_ENUM,
    PRO_QA_TYPES_MAP_FOR_LEGACY_ELECTRON,
    SESSIONS_INCIDENT_IDS,
} from './constants';
import {
    buildEventInfo,
    checkIsSavedEvent,
    Discipline,
    generateTempIncidentId,
    isTemporaryIncidentId,
    logMissingParameterWarning,
} from './helpers/pro-qa-buttons-helpers';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useProQaSessionTracker } from '../../pro-qa/use-pro-qa-session-tracker';
import { proQaResources } from '../pro-qa/pro-qa-resources';
import { useProQaSecondaryLaunchActionHandler } from './hooks/use-pro-qa-secondary-launch-action-handler';

type DisciplineSelectOptions = { isSecondarySession?: boolean };
type DisciplineSelectHandler = (
    discipline: Discipline,
    launchIncidentId?: string,
    options?: DisciplineSelectOptions
) => void;
type AgencyTypeKey = keyof typeof globalAttributes['agencyTypeGlobal'];
const PRO_QA_DISCIPLINES: Discipline[] = ['POLICE', 'FIRE', 'EMS'];

export const EventPanelFormOpenProQaButtons = observer(function EventPanelFormOpenProQaButtons({
    enabledDisciplines,
    enhancementsEnabled,
    temporaryIncidentIds,
    updateTemporaryIncidentId,
}: {
    enabledDisciplines: Discipline[];
    enhancementsEnabled?: boolean;
    temporaryIncidentIds: Record<Discipline, string | undefined>;
    updateTemporaryIncidentId: (discipline: Discipline, id: string | undefined) => void;
}) {
    const { isElectronRewrite } = useApplicationType();
    const { proQaSettings } = useBootstrapState();
    const { postMessage } = useBroadcast('legacy-store-broadcast');
    const eventForm = useIntegratedForm<EventPanelFormType>().form;
    const eventMetadataStore = useEventMetadataStore();
    const eventMetadata = eventMetadataStore.state.metadata;
    const apiClient = useApiClient();
    const proQaResource = useMemo(() => proQaResources(apiClient), [apiClient]);
    const phoneCallsStore = usePhoneCallsStore();
    const eventId = eventMetadataStore.state.metadata?.eventId;
    const agencyEventNumber = eventMetadataStore.state.metadata?.agencyEventNumber;
    const isSavedEvent = checkIsSavedEvent(eventId, agencyEventNumber);
    const savedPhoneNumberId = eventMetadataStore.state.metadata?.eventPhoneCall?.phoneNumberId;

    const { checkIfSessionWasClosed } = useProQaSessionTracker(agencyEventNumber);

    const eventLocation = useWatch({
        control: eventForm.control,
        name: 'location',
    }) as ConsolidatedLocationView | null;

    const reportingPartyFirstName = useWatch({
        control: eventForm.control,
        name: 'reportingParties.0.firstName',
    });

    const reportingPartyLastName = useWatch({
        control: eventForm.control,
        name: 'reportingParties.0.lastName',
    });

    const reportingPartyPhoneNumber = useWatch({
        control: eventForm.control,
        name: 'reportingParties.0.phoneNumber',
    });

    const phoneCall = useWatch({
        control: eventForm.control,
        name: 'phoneCall',
    });

    const phoneNumberId = savedPhoneNumberId ?? phoneCall?.phoneNumberId;

    const phoneNumberInfo = phoneNumberId
        ? phoneCallsStore.getPhoneCallByPhoneNumberId(phoneNumberId)
        : undefined;

    const initiatedDateTimeUtc = useWatch({
        control: eventForm.control,
        name: 'initiatedDateTimeUtc',
    });

    const eventInfo = useMemo(
        () =>
            buildEventInfo({
                metadata: eventMetadataStore.state.metadata ?? {},
                location: eventLocation,
                reportingParty: {
                    firstName: reportingPartyFirstName,
                    lastName: reportingPartyLastName,
                    phoneNumber: reportingPartyPhoneNumber,
                },
                phoneCallDisplay: phoneNumberInfo?.normalizedPhoneCallDisplay,
                enhancementsEnabled,
                initiatedDateTimeUtc,
            }),
        [
            eventMetadataStore.state.metadata,
            eventLocation,
            reportingPartyFirstName,
            reportingPartyLastName,
            reportingPartyPhoneNumber,
            phoneNumberInfo?.normalizedPhoneCallDisplay,
            enhancementsEnabled,
            initiatedDateTimeUtc,
        ]
    );

    const temporaryIncidentIdsRef = useRef<Record<Discipline, string | undefined>>(
        temporaryIncidentIds
    );

    useEffect(() => {
        temporaryIncidentIdsRef.current = temporaryIncidentIds;
    }, [temporaryIncidentIds]);

    const { trigger } = useIntegratedActionBroadcaster();

    const setTempIncidentId = useCallback(
        (discipline: Discipline, id: string | undefined) => {
            updateTemporaryIncidentId(discipline, id);
            temporaryIncidentIdsRef.current = {
                ...temporaryIncidentIdsRef.current,
                [discipline]: id,
            };
        },
        [updateTemporaryIncidentId]
    );

    const openProQa = useCallback(
        (type: AgencyTypeKey, launchEventInfo: EventInfo) => {
            const windowFocusConfig =
                enhancementsEnabled &&
                ((proQaSettings[
                    globalAttributes.agencyTypeGlobal[type]
                ] as unknown) as ProQaSettingForAgencyType[])?.[0]?.windowFocusHorizontal;

            const wasSessionClosed = checkIfSessionWasClosed(type);

            trigger([
                {
                    id: v4(),
                    targetWindowType: 'ROOT',
                    type: 'OPEN_PRO_QA',
                    payload: {
                        eventInfo: { ...launchEventInfo },
                        type,
                        wasSessionClosed,
                        ...(windowFocusConfig !== undefined
                            ? { windowFocusHorizontal: windowFocusConfig }
                            : {}),
                    },
                },
            ]);
        },
        [checkIfSessionWasClosed, enhancementsEnabled, proQaSettings, trigger]
    );

    const openProQALegacyElectron = useCallback(
        (type: ProQaMessageTypeLegacyElectron, launchEventInfo: EventInfo) => {
            postMessage({
                type: 'OPEN_PRO_QA',
                payload: {
                    eventInfo: { ...launchEventInfo },
                    panel: {
                        isCadNext: true,
                    },
                    type,
                },
            });
        },
        [postMessage]
    );

    const handleDisciplineSelect: DisciplineSelectHandler = useCallback(
        (discipline, launchIncidentId, options) => {
            if (!eventInfo.id && !enhancementsEnabled) {
                logMissingParameterWarning('event ID');
                return;
            }

            const trimmedLaunchIncidentId = launchIncidentId?.trim();
            const hasLaunchIncidentId = !!trimmedLaunchIncidentId;
            let resolvedIncidentId =
                trimmedLaunchIncidentId ?? temporaryIncidentIdsRef.current[discipline];
            let generatedTempIncidentId = false;
            const currentDiscipline = getDisciplineFromAgencyType(
                eventMetadata?.agencyTypeGlobalAttrId
            );
            const savedEventNumber = eventMetadata?.agencyEventNumber?.trim();
            const isSecondarySession =
                options?.isSecondarySession ??
                (currentDiscipline !== undefined && discipline !== currentDiscipline);

            if (hasLaunchIncidentId) {
                if (isTemporaryIncidentId(trimmedLaunchIncidentId)) {
                    setTempIncidentId(discipline, trimmedLaunchIncidentId);
                } else {
                    // secondary launches on saved events can provide agency event numbers,
                    // keep only generated temporary IDs in session state
                    setTempIncidentId(discipline, undefined);
                }
            }
            if (isSavedEvent && !hasLaunchIncidentId) {
                const fallbackIncident =
                    savedEventNumber ||
                    (eventInfo.incidentNumber
                        ? String(eventInfo.incidentNumber).trim()
                        : undefined);
                if (fallbackIncident) {
                    resolvedIncidentId = fallbackIncident;
                    setTempIncidentId(discipline, undefined);
                }
            }

            if (!resolvedIncidentId) {
                resolvedIncidentId = generateTempIncidentId();
                setTempIncidentId(discipline, resolvedIncidentId);
                generatedTempIncidentId = true;
            }

            const launchEventInfo = {
                ...eventInfo,
                incidentNumber: resolvedIncidentId,
                secondarySession: isSecondarySession || undefined,
            };

            if (isElectronRewrite) {
                openProQa(discipline, launchEventInfo);
            } else {
                openProQALegacyElectron(
                    PRO_QA_TYPES_MAP_FOR_LEGACY_ELECTRON[discipline],
                    launchEventInfo
                );
            }

            if (isSavedEvent && discipline !== currentDiscipline && generatedTempIncidentId) {
                setTempIncidentId(discipline, undefined);
            }
        },
        [
            enhancementsEnabled,
            eventInfo,
            eventMetadata?.agencyEventNumber,
            eventMetadata?.agencyTypeGlobalAttrId,
            isSavedEvent,
            isElectronRewrite,
            openProQa,
            openProQALegacyElectron,
            setTempIncidentId,
        ]
    );

    useProQaSecondaryLaunchActionHandler({
        eventId: eventMetadata?.eventId,
        agencyEventNumber: eventMetadata?.agencyEventNumber,
        temporaryIncidentIdsRef,
        handleDisciplineSelect,
        consumePendingSecondaryLaunches: proQaResource.consumePendingSecondaryLaunches,
    });

    useEffect(
        function overwriteTemporaryIncidentIdsWithSavedEventNumber() {
            const currentEventId = eventMetadata?.eventId;
            if (!currentEventId) {
                // not a saved event
                return;
            }

            const resolvedIncidentNumber = eventMetadata?.agencyEventNumber?.trim();
            if (!resolvedIncidentNumber) {
                return;
            }

            // ensure any persisted temp ids is reused (e.g. after reload) before considering new ones
            const storedTempIds = storageHelpers.getSessionItem<
                Record<Discipline, string | undefined>
            >(SESSIONS_INCIDENT_IDS);

            PRO_QA_DISCIPLINES.forEach((discipline) => {
                const tempId = (
                    temporaryIncidentIds[discipline] ?? storedTempIds?.[discipline]
                )?.trim();
                if (!tempId) {
                    return;
                }

                if (!isTemporaryIncidentId(tempId)) {
                    setTempIncidentId(discipline, undefined);
                    return;
                }

                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'ROOT',
                        type: 'OVERWRITE_PRO_QA_INCIDENT_NUMBER',
                        payload: {
                            type: discipline,
                            incidentNumber: tempId,
                            overIncidentNumber: resolvedIncidentNumber,
                        },
                    },
                ]);
                setTempIncidentId(discipline, undefined);
            });
        },
        [
            eventMetadata?.eventId,
            eventMetadata?.agencyEventNumber,
            temporaryIncidentIds,
            trigger,
            setTempIncidentId,
        ]
    );

    if (!enhancementsEnabled) {
        return (
            <ButtonGroup>
                {enabledDisciplines.map((discipline) => (
                    <Button
                        variant="outline"
                        trailingVisual="OpenNewWindow"
                        key={discipline}
                        onClick={() => handleDisciplineSelect(discipline)}
                    >
                        {DISCIPLINE_LABEL_ENUM[discipline].initLabel} ProQA
                    </Button>
                ))}
            </ButtonGroup>
        );
    }

    if (enabledDisciplines.length === 1) {
        const discipline = enabledDisciplines[0];
        return (
            <Button variant="outline" onClick={() => handleDisciplineSelect(discipline)}>
                {DISCIPLINE_LABEL_ENUM[discipline].initLabel} ProQA
            </Button>
        );
    }

    return (
        <Menu>
            <MenuTrigger asChild>
                <Button variant="outline" trailingVisual="Open">
                    Open ProQA
                </Button>
            </MenuTrigger>
            <MenuContent>
                {enabledDisciplines.map((discipline) => (
                    <MenuItem key={discipline} onClick={() => handleDisciplineSelect(discipline)}>
                        {DISCIPLINE_LABEL_ENUM[discipline].enhancementLabel}
                    </MenuItem>
                ))}
            </MenuContent>
        </Menu>
    );
});

function getDisciplineFromAgencyType(agencyTypeGlobalAttrId?: number): Discipline | undefined {
    const { agencyTypeGlobal } = globalAttributes;
    switch (agencyTypeGlobalAttrId) {
        case agencyTypeGlobal.POLICE:
            return 'POLICE';
        case agencyTypeGlobal.FIRE:
            return 'FIRE';
        case agencyTypeGlobal.EMS:
            return 'EMS';
        default:
            return undefined;
    }
}
